import { useQuery } from '@apollo/client'
import { Grid, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { STAKE_ADDRESS } from '@/constants/blockchain'
import { useEconomicsContext } from '@/contexts/EconomicsContext'
import { NumberStakers, QUERY_GET_NUMBER_STAKERS } from '@/schemas/staking.schema'
import { formatNumberWithCommas, processBalance } from '@/utils/utils'

import DataCard from '../Cards/DataCard'
import ErrorMessage from '../ErrorMessage'
import Loading from '../Loading'

const Supply: React.FC = () => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const isDownSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const {
    totalStaked,
    ll1Unlockable,
    ll1UnlockRate,
    rl1Unlockable,
    rl1UnlockRate,
    locked,
    circulating,
    foundation,
    airdrop,
    liquid,
    othersBalance,
    isLoading,
    error: errorEconomics,
  } = useEconomicsContext()

  // Sum Others and Foundation balances
  const totalFoundation = useMemo(
    () => processBalance(foundation.value + othersBalance.value),
    [foundation, othersBalance]
  )

  // Compute staked percentage
  const stakedPercentage = useMemo(() => {
    const totalStakedValue = parseFloat(totalStaked.value.toString())
    const liquidValue = parseFloat(liquid.value.toString())
    return (100 * totalStakedValue) / (totalStakedValue + liquidValue)
  }, [totalStaked, liquid])

  // Compute held in staking/RL1/LL1
  const heldInStaking = useMemo(
    () => processBalance(totalStaked.value + ll1Unlockable.value + rl1Unlockable.value),
    [totalStaked, ll1Unlockable, rl1Unlockable]
  )

  // Get number of stakers
  const {
    loading: loadingQuery,
    error: errorQuery,
    data,
  } = useQuery<NumberStakers>(QUERY_GET_NUMBER_STAKERS, {
    variables: {
      stakeAddr: STAKE_ADDRESS.toLowerCase(),
    },
    defaultOptions: {
      pollInterval: 60000,
    },
  })

  const numStakers = useMemo(
    () => (data ? formatNumberWithCommas(data.total.agg.count) : '-'),
    [data]
  )

  // Loading and error handling
  const loading = useMemo(() => isLoading || loadingQuery, [isLoading, loadingQuery])
  const error = useMemo(
    () => ({
      isError: errorEconomics.isError || errorQuery !== undefined,
      call: 'Economics',
      msg: errorQuery?.message || '',
    }),
    [errorEconomics, errorQuery]
  )

  return (
    <Stack style={{ gap: '15px' }}>
      <Title order={isDownSm ? 3 : 2}>{t('components.staking.supply.title', 'L1 Supply')}</Title>
      <Text style={{ textAlign: 'left' }}>
        {t(
          'components.staking.supply.description',
          'Understand the balance and movement of L1 at a glance — the foundation of our thriving digital economy.'
        )}
      </Text>
      {loading && <Loading />}
      {error.isError && (
        <ErrorMessage
          message={t(
            'components.staking.supply.errorLoadingEconomics',
            'RPC Error loading {{ call }}',
            {
              call: error.call,
            }
          )}
          details={error.msg}
        />
      )}
      {!loading && !error.isError && (
        <>
          <Grid
            gutter={{ base: '30px' }}
            style={{
              backgroundColor: isMobile ? theme.colors.background2[0] : '',
              borderRadius: 12,
              padding: isMobile ? '30px' : '',
              margin: '0px 5px',
            }}
          >
            <Grid.Col span="content">
              <DataCard
                title={t('components.staking.networkMetrics.metric1', 'Staked Supply')}
                value={`${stakedPercentage.toFixed(2)}%`}
                tooltip={t(
                  'components.staking.networkMetrics.metric1Tooltip',
                  'The percentage of L1 that is currently staked by LAMINA1 token holders and are actively generating rewards on the LAMINA1 network, plus completed stakes that can be withdrawn by users on the network. Note that the Staking percentage is calculated based on the Current Liquid L1, not the total Circulating Supply.'
                )}
                description={[
                  `${totalStaked.fullDisplay} L1 locked in staking across ${numStakers} participants`,
                ]}
              />
            </Grid.Col>
            <Grid.Col span="content">
              <DataCard
                title={t('components.staking.supply.circulatingSupply', 'Circulating Supply')}
                value={circulating.fullDisplay}
                description={[
                  `Current Liquid L1: ${liquid.fullDisplay}`,
                  `Held by Foundation: ${totalFoundation.fullDisplay}`,
                  `Held for Airdrops: ${airdrop.fullDisplay}`,
                  `Locked in Contracts: ${heldInStaking.fullDisplay}`,
                ]}
                tooltip={t(
                  'components.staking.supply.circulatingSupplyTooltip',
                  'The number of L1 tokens released on the network that are currently useable on LAMINA1 and available to users. Note that some L1 tokens are locked in Stake/LL1/RL1 contracts and cannot be used unless the user unlocks them (by unstaking or unwrapping RL1/LL1).'
                )}
              />
            </Grid.Col>
            <Grid.Col span="content">
              <DataCard
                title={t('components.staking.supply.lockedSupply', 'Locked Supply')}
                value={locked.fullDisplay}
                description={[
                  `${rl1Unlockable.fullDisplay} RL1 unlockable`,
                  `${ll1Unlockable.fullDisplay} LL1 unlockable`,
                  `RL1 Unlock Rate: ${rl1UnlockRate.toFixed(2)}% per day`,
                  `LL1 Unlock Rate: ${ll1UnlockRate.toFixed(2)}% per day`,
                ]}
                tooltip={t(
                  'components.staking.supply.lockedSupplyTooltip',
                  'L1 tokens that are currently locked in smart contracts and inaccessible to L1 users. They are either held by the Foundation in treasury or distributed to recipients. These tokens unlock at a quarterly rate set by the Open Metaverse Monetary Authority, with active inputs from the LAMINA1 community.'
                )}
              />
            </Grid.Col>
            <Grid.Col span="content">
              <DataCard
                title={t('components.staking.supply.supplyCap', 'Supply Cap')}
                value="1,500,000,000"
                tooltip={t(
                  'components.staking.supply.totalSupplyTooltip',
                  'The total amount of tokens currently minted on the LAMINA1 network, including those that have been released on the network and those that are held in treasury by the Open Metaverse Monetary Authority.'
                )}
              />
            </Grid.Col>
          </Grid>
        </>
      )}
    </Stack>
  )
}

export default Supply
